/// FOURJS_START_COPYRIGHT(D,2024)
/// Property of Four Js*
/// (c) Copyright Four Js 2024, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

//Required by ESlint not to raise errors
"use strict";

modulum(
  "ApplicationListService",
  ["InitService", "MainContainerService", "NavigationButtonService"],
  /**
   * @param {gbc} context
   * @param {classes} cls
   */
  function (context, cls) {
    /**
     * Application list service dedicated to navigation management
     * Main role of the service is to display app item (icon & text) for each running application in the sidebar and the floating panel
     * @namespace gbc.ApplicationListService
     * @gbcService
     */
    class ApplicationListService /** @lends gbc.ApplicationListService */ {
      static DefaultVisibility = "auto";
      static DefaultPosition = "left";
      constructor() {
        /** @type {String} */
        this._categoryName = "apps";

        /** @type {classes.ItemWidget} */
        this._categoryTitleWidget = null;
        /**
         * list of active applications
         * @type {Map<string, classes.ItemWidget>}
         */
        this._appList = null;
        /**
         * Currently focused app item
         * @type {classes.ItemWidget}
         */
        this._currentAppItem = null;
        /**
         * 4ST style to define app list position
         * @type {?string}
         */
        this._applicationListPosition = null;
        /**
         * 4ST style to define app list visibility settings
         * @type {?string}
         */
        this._applicationListVisible = null;
        /**
         * Current session navigation manager
         * @type {classes.VMSessionNavigationManager}
         * @private
         */
        this._navigationManager = null;
      }

      /**
       * @inheritDoc
       */
      init() {
        this._appList = new Map();
        this._applicationListVisible = cls.WindowHelper.isMobile() ? "no" : ApplicationListService.DefaultVisibility; // default value is auto
        this._applicationListPosition = ApplicationListService.DefaultPosition; // default value is left
        // Once the session is started and registered as Current Session, we can listen to it
        context.SessionService.onSessionAdded((evt, invoker, session) => {
          if (this.isApplicationListInChromeBar()) {
            context.MainContainerService.getChromeBarMiddleContainer().replaceChildren();
          }
          this._navigationManager = session.getNavigationManager();
          this._navigationManager.onApplicationAdded(this._onNewApplication.bind(this));
          this._navigationManager.onApplicationRemoved(this._onApplicationClose.bind(this));
          this._navigationManager.onApplicationSwitched(this._onApplicationSwitch.bind(this));
        });
        context.MainContainerService.when(context.constants.widgetEvents.sideBarDrawerDocked, this._hideAppList.bind(this));
        context.MainContainerService.when(context.constants.widgetEvents.sideBarDrawerUndocked, this._checkAppListVisibility.bind(this));
        context.MainContainerService.when(context.constants.widgetEvents.sideBarDrawerUpdated, this._checkAppListVisibility.bind(this));
      }

      /**
       * Set 'applicationListVisible' and 'applicationListPosition' 4ST values
       * @param {classes.NodeBase} application
       */
      initVisibilityAndPosition(application) {
        this._applicationListVisible = application.getStyleAttribute("applicationListVisible") || ApplicationListService.DefaultVisibility; // use "auto" as default if not specified
        this._applicationListPosition = application.getStyleAttribute("applicationListPosition") || ApplicationListService.DefaultPosition; // use "left" as default if not specified
        if (cls.WindowHelper.isMobile()) {
          // mobile forced values
          this._applicationListVisible = "no";
          this._applicationListPosition = ApplicationListService.DefaultPosition;
        }
        this._moveAppList();
        this._checkAppListVisibility();
      }

      /**
       * @inheritDoc
       */
      destroy() {
        if (this._appList) {
          for (const item of this._appList.values()) {
            item.destroy();
          }
          this._appList.clear();
          this._updateSidebarAppCounter();
        }
        if (this._currentAppItem && !this._currentAppItem.isDestroyed()) {
          this._currentAppItem.destroy();
        }
        this._currentAppItem = null;
        if (this._categoryTitleWidget && !this._categoryTitleWidget.isDestroyed()) {
          this._categoryTitleWidget.destroy();
        }
        this._categoryTitleWidget = null;
        this._navigationManager = null;
        context.NavigationButtonService.removeAction(this._categoryName, gbc.systemAppId);
      }

      /**
       * Add an application in sidebars (rail + drawer)
       * @param {classes.VMApplication} app
       */
      add(app) {
        const appWidget = app.getUI().getWidget();
        const buildParameters = appWidget.getBuildParameters();
        const appItem = cls.WidgetFactory.createWidget("Item", buildParameters);
        appItem.setAsListItem();
        appItem.setAcceptEventWhenWindowInactive(true);
        appItem.setSecondaryIcon(context.constants.widgetCssClasses.close);
        appItem.addClasses("gbc_appItem");
        appItem.onClick((domEvent) => {
          appWidget.requestDisplay();
        });
        appItem.onClickSecondaryIcon((domEvent) => {
          app.getUI().getWidget().getUserInterfaceWidget().getActiveWindowWidget()?.emitClose();
        });

        // store new created app in service apps list
        this._appList.set(app.applicationHash, appItem);
        app.when(context.constants.widgetEvents.titleChanged, (evt, src, title) => {
          appItem.setText(title);
          appItem.setTitle(title);
          if (appItem.getImageWidget()) {
            appItem.setPrimaryIcon(appItem.getImageWidget().getSrc());
          } else {
            appItem.setPrimaryIconText(this._generateIconText(title));
          }
        });
        app.when(context.constants.widgetEvents.iconChanged, (evt, src, icon) => {
          appItem.setPrimaryIcon(icon);
        });

        // Init sidebar drawer with options buttons
        context.MainContainerService.initSideBarButtons();
        // Add app item in sidebar rail or chromebar
        // applicationListVisible=no takes priority: never add items to ChromeBar if list is hidden
        const appListItem = cls.DOMElementHelper.wrapElementIntoListItem("gbc_appItem", appItem.getElement());
        if (this.isApplicationListInChromeBar() && this._applicationListVisible !== "no") {
          // ChromeBar middle is a <ul role="tablist"> → each app is a tab inside an <li role="presentation">.
          // We target this specific clone via the canonical DOMElementHelper rather than ItemWidget's
          // setAriaRole/setAriaAttribute, which propagate to every clone (including the sidebar drawer
          // copy created below) — the tablist semantic only applies to the chromebar mount.
          cls.DOMElementHelper.updateAttribute(appListItem, "role", "presentation");
          cls.DOMElementHelper.updateAttribute(appItem.getElement(), "role", "tab");
          cls.DOMElementHelper.updateAttribute(appItem.getElement(), "aria-selected", appItem === this._currentAppItem ? "true" : "false");
          context.MainContainerService.addChromeBarMiddleContainerItems(appListItem);
        } else {
          context.MainContainerService.addSideBarRailItems(appListItem);
        }
        // Add app item in sidebar drawer
        if (context.ThemeService.getValue("gbc-SideBarDrawer-AppsSection-show")) {
          context.MainContainerService.addSideBarDrawerItems(
            this._categoryName,
            cls.DOMElementHelper.wrapElementIntoListItem("gbc_appItem", appItem.cloneElement())
          );
        }

        this._checkAppListVisibility();
        this._updateSidebarAppCounter();
      }

      /**
       * Generate app icon text from app name
       * @param {string} appName
       * @returns {string}
       */
      _generateIconText(appName) {
        if (!appName) return "";

        // Split the app name into words by treating any non-letter and non-number character as a separator.
        const words = appName.split(/[^\p{L}\p{N}]+/u).filter(Boolean);

        if (words.length >= 3) {
          // Take first letter of first 3 words
          return words
            .slice(0, 3)
            .map((w) => w[0])
            .join("")
            .toUpperCase();
        }

        // If less than 3 words, take first 3 letters from cleaned full string
        const flattened = words.join("");
        return flattened.slice(0, 3).toUpperCase();
      }

      /**
       * Remove an application from sidebars
       * @param {classes.VMApplication} app
       */
      remove(app) {
        const identifier = app.applicationHash;
        const appItem = this._appList.get(identifier);
        if (!appItem) {
          return;
        }

        this._appList.delete(identifier);
        appItem.destroy();

        this._checkAppListVisibility();
        this._updateSidebarAppCounter();
      }

      /**
       * Update current focused app item
       * @param {classes.ItemWidget} appItem
       */
      setCurrentAppItem(appItem) {
        const inTabList = this.isApplicationListInChromeBar();
        if (this._currentAppItem && !this._currentAppItem.isDestroyed() && this._currentAppItem !== appItem) {
          this._currentAppItem.setCurrent(false);
          if (inTabList) {
            // Keep aria-selected on the chromebar tab in sync with the visual "current" state.
            // Targets only the chromebar clone — see add() for the rationale.
            cls.DOMElementHelper.updateAttribute(this._currentAppItem.getElement(), "aria-selected", "false");
          }
        }

        this._currentAppItem = appItem;
        if (this._currentAppItem) {
          this._currentAppItem.setCurrent(true);
          if (inTabList) {
            cls.DOMElementHelper.updateAttribute(this._currentAppItem.getElement(), "aria-selected", "true");
            // In tab pattern aria-selected already conveys "current" — aria-current on the same element would be redundant
            cls.DOMElementHelper.updateAttribute(this._currentAppItem.getElement(), "aria-current", null);
          }
        }
      }

      /**
       * Get current app item
       * @return {classes.ItemWidget}
       */
      getCurrentAppItem() {
        return this._currentAppItem;
      }

      /**
       * Update closable status of app item depending of current dialog close action
       * Needed to update quick close icon
       * @param {classes.VMApplication} app
       * @param closable
       */
      setAppClosable(app, closable) {
        const appItem = this._appList.get(app.applicationHash);
        if (!appItem) {
          return;
        }
        appItem.toggleClass("gbc_closable", closable);
      }

      /**
       * Returns true if the application list position is set to the chromebar
       * @return {boolean}
       */
      isApplicationListInChromeBar() {
        return this._applicationListPosition === "top" && context.MainContainerService.isChromeBarVisible();
      }

      /**
       * Set Applist position (for 4ST api)
       * @param {String} position could be 'left' or 'top'
       * Returns true if the application list position is set to the chromebar
       * @return {boolean}
       */
      setApplicationListPosition(position = ApplicationListService.DefaultPosition) {
        const changed = this._applicationListPosition !== position;
        this._applicationListPosition = position;
        if (changed) {
          this._moveAppList();
          if (this.isApplicationListInChromeBar()) {
            context.WindowListService.disableWindowList();
          }
        }
        this._checkAppListVisibility();
      }

      /**
       * Set Applist mode (for 4ST api)
       * @param {String} visible could be 'yes', 'no' or 'auto'
       */
      setApplicationListVisible(visible = ApplicationListService.DefaultVisibility) {
        if (visible !== "no" && context.MainContainerService.shouldAutoDockSideBarDrawer()) {
          return;
        }
        this._applicationListVisible = visible;
        this._checkAppListVisibility();
      }

      /**
       * Display sidebar rail
       * @private
       */
      _showAppList() {
        if (context.MainContainerService.isSideBarDrawerDocked()) {
          // if docked show app list via sidebardrawer otherwise with sidebarrail
          context.MainContainerService.showSideBarDrawer();
        } else {
          context.MainContainerService.showSideBarRail();
        }
        context.NavigationButtonService.removeAction(this._categoryName, gbc.systemAppId);
      }

      /**
       * Hide sidebar rail
       * @private
       */
      _hideAppList() {
        context.MainContainerService.hideSideBarRail();
        if (this._appList.size === 0 || context.MainContainerService.isSideBarDrawerEmpty()) {
          return;
        }
        context.NavigationButtonService.addAction(
          this._categoryName,
          gbc.systemAppId,
          context.MainContainerService.toggleSideBarDrawer.bind(context.MainContainerService)
        );
      }

      /**
       * Move application list between sidebarrail and chromebar
       * @private
       */
      _moveAppList() {
        if (!context.MainContainerService.isChromeBarVisible()) {
          return;
        }
        // when application is top, we need middle container of chromebar to be able to shrink when max width is reached
        // applicationListVisible=no takes priority: never move items to ChromeBar if list is hidden
        const shouldBeInChromeBar = this.isApplicationListInChromeBar() && this._applicationListVisible !== "no";
        context.MainContainerService.getChromeBarMiddleContainer().classList.toggle("flex-shrink", shouldBeInChromeBar);
        if (shouldBeInChromeBar && !context.MainContainerService.isSideBarRailEmpty()) {
          // move previously added application from sidebarrail to chromebar
          context.MainContainerService.addChromeBarMiddleContainerItems(...context.MainContainerService.getSideBarRailContainer().children);
        } else if (!shouldBeInChromeBar && context.MainContainerService.isSideBarRailEmpty()) {
          // firstChild is UL
          context.MainContainerService.addSideBarRailItems(...context.MainContainerService.getChromeBarMiddleContainer().firstChild.children);
        }
      }

      /**
       * Check if app list should be displayed or not in sidebar rail (depending on 4ST style)
       * @private
       */
      _checkAppListVisibility() {
        const hasStartMenu = context.MainContainerService.getSideBarDrawerActiveCategories().has("other");
        const singleAppWithoutStartMenu = this._appList.size <= 1 && !hasStartMenu;
        const chromeBarHidden = !context.MainContainerService.isChromeBarVisible();
        const sidebarDocked = context.MainContainerService.isSideBarDrawerDocked();

        // Show app list if chrome bar is hidden and start menu is present
        if (chromeBarHidden && hasStartMenu) {
          this._showAppList();
          return;
        }

        // Hide app list if sidebar is docked, ensure drawer is visible
        if (sidebarDocked) {
          context.MainContainerService.showSideBarDrawer();
          this._hideAppList();
        }

        let appListVisibility = this._applicationListVisible;
        if (this.isApplicationListInChromeBar()) {
          appListVisibility = "no";
        }
        // Handle visibility based on 4ST setting
        switch (appListVisibility) {
          case "no":
            this._hideAppList();
            break;
          case "yes":
            this._showAppList();
            break;
          default:
            if (singleAppWithoutStartMenu) {
              this._hideAppList();
            } else {
              this._showAppList();
            }
        }

        // Toggle apps category and sidebar drawer based on app/start menu presence
        if (singleAppWithoutStartMenu) {
          context.NavigationButtonService.hideAppsCategory();
          context.MainContainerService.hideSideBarDrawer();
        } else {
          context.NavigationButtonService.showAppsCategory();
        }
      }

      /**
       * Updates the secondary icon text (used as a counter) in the sidebar drawer category title
       */
      _updateSidebarAppCounter() {
        if (!this._categoryTitleWidget) {
          const titleElement = context.MainContainerService.getSideBarDrawerCategoryTitle(this._categoryName);
          if (!titleElement) {
            return;
          }
          this._categoryTitleWidget = gbc.WidgetService.getWidgetFromElement(titleElement);
        }

        this._categoryTitleWidget.setSecondaryIconText(this._appList.size);
      }

      //#region Event Management

      /**
       * Call when a new application is started
       * @param {VMApplication} application
       */
      _onNewApplication(evt, invoker, application) {
        // Don't add the application in the list if it's not standalone
        if (!application.isAwaited()) {
          this.add(application);
        } else if (context.ThemeService.getValue("mt-sidebar-show-child-name")) {
          // RUN WITH WAITING + show child name set in theme variable
          // set child app name on parent app item
          const parentApp = this._navigationManager.getParentApplication(application);
          if (parentApp) {
            const parentAppItem = this._appList.get(parentApp.applicationHash);
            if (parentAppItem) {
              application.when(context.constants.widgetEvents.titleChanged, (event, src, title) => {
                parentApp.emit(context.constants.widgetEvents.titleChanged, title);
              });
              application.when(context.constants.widgetEvents.iconChanged, (event, src, icon) => {
                parentApp.emit(context.constants.widgetEvents.iconChanged, icon);
              });
            }
          }
        }
      }

      /**
       * Call when an application is closed
       * @param {VMApplication} application
       */
      _onApplicationClose(evt, invoker, application) {
        this.remove(application);
        if (application.isAwaited() && context.ThemeService.getValue("mt-sidebar-show-child-name")) {
          // RUN WITH WAITING + show child name set in theme variable
          // rollback parent app title on parent app item
          const parentApp = this._navigationManager.getParentApplication(application);
          if (parentApp) {
            const parentAppItem = this._appList.get(parentApp.applicationHash);
            if (parentAppItem) {
              parentApp.emit(context.constants.widgetEvents.titleChanged, parentApp.getTitle());
              if (parentApp.getImage()) {
                parentApp.emit(context.constants.widgetEvents.iconChanged, parentApp.getImage());
              }
            }
          }
        }
      }

      /**
       * Call when current application have change
       * @param {VMApplication} newApplication New Current application
       * @param {VMApplication} oldApplication Previous current application
       */
      _onApplicationSwitch(evt, invoker, currentApplication, previousApplication) {
        // If the application is awaited
        // just skip, as GBC will display the parent application
        // updating the current item
        if (currentApplication.isAwaited()) {
          return;
        }
        context.MainContainerService.hideFloatingElements();
        const appItem = this._appList.get(currentApplication.applicationHash);
        // update current app item in sidebar rail
        this.setCurrentAppItem(appItem);
      }

      //#endregion
    }

    gbc.ApplicationListService = new ApplicationListService();
    context.InitService.register(context.ApplicationListService);
  }
);
